import type { Ref } from "vue";
import {
  FACET_GROUPS,
  decodeBestEffort,
  encode,
  type FacetGroupEntry,
  type QueryObject,
  type Selection,
} from "./filter-url";

export type CatalogFacetValue = {
  key: string;
  aliases?: string[] | null;
  label?: string | null;
  count: number;
  selected: boolean;
  disabled: boolean;
  swatch?: string | null;
  image?: string | null;
};

export type CatalogFacetDescriptor = {
  key: string;
  label?: string | null;
  helpText?: string | null;
  dataType: string;
  uiType: string;
  multiSelect: boolean;
  values?: CatalogFacetValue[] | null;
  availableMin?: string | null;
  availableMax?: string | null;
  selectedMin?: string | null;
  selectedMax?: string | null;
  step?: string | null;
  unit?: string | null;
};

export type CatalogSearchIssue = {
  code: string;
  message: string;
};

export type CatalogSearchPayload = {
  schemaVersion: number;
  requestId: string;
  status: "OK" | "DEGRADED" | "UNAVAILABLE" | string;
  statusReason?: string | null;
  generationId?: string | null;
  mappingRevision?: string | null;
  indexLagSeconds?: number | null;
  items?: any[] | null;
  total: number;
  page: number;
  pageSize: number;
  sort?: string | null;
  facets?: CatalogFacetDescriptor[] | null;
  applied?: Array<{
    key: string;
    values?: string[] | null;
    min?: string | null;
    max?: string | null;
  }> | null;
  errors?: CatalogSearchIssue[] | null;
  warnings?: CatalogSearchIssue[] | null;
};

export type FilterOrchestratorContext = {
  capability: Ref<boolean | null>;
  facets: Ref<CatalogFacetDescriptor[]>;
  status: Ref<string>;
  error: Ref<string | null>;
  loading: Ref<boolean>;
  requestId: Ref<string>;
  generationId: Ref<string>;
  mappingRevision: Ref<string>;
  retry: () => void | Promise<void>;
};

const STABLE_KEY = /^[a-z][a-z0-9_]*$/;
const RANGE_KEY = /^([a-z][a-z0-9_]*)_(min|max)$/;
const LEGACY_OR_STRUCTURAL_KEYS = new Set([
  "page",
  "search",
  "sort",
  "order",
  "size",
  "category",
  "filters",
  "min_price",
  "max_price",
  "price_min",
  "price_max",
]);
// Campaign and click-id parameters that ad platforms and email tools append
// to landing URLs. Treating one of them as a facet candidate makes the
// server reject the whole selection and the category shows no products
// (e.g. Google Ads' gad_source=1).
const NON_FILTER_PREFIXES = [
  "utm_",
  "min_filters_",
  "max_filters_",
  "gclid",
  "gclsrc",
  "gad",
  "gbraid",
  "wbraid",
  "dclid",
  "srsltid",
  "fbclid",
  "msclkid",
  "ttclid",
  "twclid",
  "li_fat_id",
  "mc_cid",
  "mc_eid",
  "igshid",
  "yclid",
  "epik",
  "irclickid",
  "hsa_",
  "sccid",
  "rdt_cid",
  "s_kwcid",
  "ref_",
];

const valuesOf = (value: unknown): string[] =>
  (Array.isArray(value) ? value : value == null ? [] : [value])
    .map((item) => String(item).trim())
    .filter(Boolean);

const isStableValueList = (value: unknown) => {
  const values = valuesOf(value);
  return (
    values.length > 0 &&
    values.every((item) => /^[a-z0-9][a-z0-9_-]*$/.test(item))
  );
};

const inferredKind = (facet: CatalogFacetDescriptor): "checkbox" | "range" => {
  const hint = `${facet.dataType || ""} ${facet.uiType || ""}`.toLowerCase();
  return /range|slider|number|numeric|decimal/.test(hint)
    ? "range"
    : "checkbox";
};

const syntheticGroupId = (key: string) => {
  let hash = 2166136261;
  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return 1_000_000 + (hash >>> 0);
};

/**
 * Builds the codec map from server descriptors. FACET_GROUPS is only a
 * migration/default map: a newly published stable facet is accepted without
 * a Vue release as soon as catalogSearchV1 describes it.
 */
export const buildRuntimeFacetGroupMap = (
  facets: CatalogFacetDescriptor[] = [],
  query: QueryObject = {}
): FacetGroupEntry[] => {
  const entries = new Map<string, FacetGroupEntry>(
    FACET_GROUPS.map((entry) => [entry.key, entry])
  );

  for (const facet of facets || []) {
    const key = String(facet?.key || "").trim();
    if (!STABLE_KEY.test(key) || key === "price") continue;
    const known = entries.get(key);
    entries.set(key, {
      key,
      groupId: known?.groupId ?? syntheticGroupId(key),
      kind: inferredKind(facet),
      confirmed: true,
      note: known?.note || "Published dynamically by catalogSearchV1.",
    });
  }

  // A bookmark can contain a facet created after this frontend build. Let
  // the server validate safe snake_case candidates on the first request;
  // its descriptor becomes authoritative for every subsequent interaction.
  for (const [rawKey, rawValue] of Object.entries(query || {})) {
    const key = String(rawKey).toLowerCase();
    if (
      LEGACY_OR_STRUCTURAL_KEYS.has(key) ||
      NON_FILTER_PREFIXES.some((prefix) => key.startsWith(prefix))
    ) {
      continue;
    }

    const rangeMatch = key.match(RANGE_KEY);
    if (rangeMatch && Number.isFinite(Number(valuesOf(rawValue)[0]))) {
      const baseKey = rangeMatch[1];
      if (!entries.has(baseKey)) {
        entries.set(baseKey, {
          key: baseKey,
          groupId: syntheticGroupId(baseKey),
          kind: "range",
          confirmed: false,
          note: "Safe URL candidate awaiting the server descriptor.",
        });
      }
      continue;
    }

    if (
      STABLE_KEY.test(key) &&
      isStableValueList(rawValue) &&
      !entries.has(key)
    ) {
      entries.set(key, {
        key,
        groupId: syntheticGroupId(key),
        kind: "checkbox",
        confirmed: false,
        note: "Safe URL candidate awaiting the server descriptor.",
      });
    }
  }

  return [...entries.values()];
};

/**
 * Facets whose value aliases are not OpenCart filter ids. A legacy
 * `filters=`/`filter=` id is an `oc_filter.filter_id` (the header menu builds
 * those links from api/filters), but Filter Orchestrator sources `brand` from
 * the OpenCart Manufacturer, so its aliases are manufacturer ids, and
 * `in_stock` from the stock status ("1"/"0"). Those numbers overlap with
 * filter ids (12 is the Black frame colour and Versace; 27 is Cat Eye and
 * Ray-Ban), so they never resolve a legacy id. The storefront itself writes a
 * brand selection as `brand=<key>`, which does not go through this index.
 */
export const NON_FILTER_ID_ALIAS_FACETS: ReadonlySet<string> = new Set([
  "brand",
  "in_stock",
]);

/**
 * Maps legacy flat option ids to the facet group that owns them. An id is
 * indexed only when exactly one filter-id facet claims it; an ambiguous or
 * unclaimed id stays unmapped, so category.vue takes its fail-closed legacy
 * path (OpenCart applies its own filter id) instead of guessing a facet.
 */
const buildLegacyOptionGroupIndex = (
  facets: CatalogFacetDescriptor[],
  byKey: Map<string, FacetGroupEntry>
) => {
  const owners = new Map<number, Set<number>>();
  for (const facet of facets || []) {
    const key = String(facet?.key || "");
    if (NON_FILTER_ID_ALIAS_FACETS.has(key)) continue;
    const group = byKey.get(key);
    if (!group || group.kind !== "checkbox") continue;
    for (const value of facet.values || []) {
      const numericAliases = [value?.key, ...(value?.aliases || [])]
        .map((candidate) => String(candidate || "").trim())
        .filter((candidate) => /^\d+$/.test(candidate));
      for (const optionId of numericAliases) {
        const id = Number(optionId);
        if (!owners.has(id)) owners.set(id, new Set());
        owners.get(id)!.add(group.groupId);
      }
    }
  }
  const optionGroupIndex = new Map<number, number>();
  for (const [optionId, groups] of owners) {
    if (groups.size === 1) optionGroupIndex.set(optionId, [...groups][0]);
  }
  return optionGroupIndex;
};

export const decodeCatalogSelection = (
  query: QueryObject,
  facets: CatalogFacetDescriptor[] = []
): { selection: Selection; groupMap: FacetGroupEntry[] } => {
  const groupMap = buildRuntimeFacetGroupMap(facets, query);
  const byKey = new Map(groupMap.map((entry) => [entry.key, entry]));
  const optionGroupIndex = buildLegacyOptionGroupIndex(facets, byKey);
  return {
    selection: decodeBestEffort(query, { groupMap, optionGroupIndex })
      .selection,
    groupMap,
  };
};

export const hasAppliedCatalogFilters = (
  query: QueryObject,
  facets: CatalogFacetDescriptor[] = []
) => {
  const { selection } = decodeCatalogSelection(query, facets);
  return (
    Object.values(selection.facets).some((values) => values.length > 0) ||
    Object.values(selection.ranges).some(
      (range) => range.min !== undefined || range.max !== undefined
    ) ||
    selection.price.min !== undefined ||
    selection.price.max !== undefined
  );
};

/**
 * True when the URL asks for any filter, including legacy flat ids that no
 * facet maps (category.vue still applies those through the legacy API). Used
 * to keep error copy neutral on a plain listing.
 */
export const hasRequestedCatalogFilters = (
  query: QueryObject,
  facets: CatalogFacetDescriptor[] = []
) =>
  hasAppliedCatalogFilters(query, facets) ||
  valuesOf(query?.filters).length > 0 ||
  valuesOf(query?.filter).length > 0;

/**
 * Rebuilds the filter portion of a route from the server-authoritative
 * `applied` response. This is the compatibility bridge for old bookmarks:
 * the request may use a numeric option alias (for example frame_shape=20),
 * while the response reports the canonical stable value (`square`).
 *
 * Search, sort, page and non-filter navigation/tracking parameters are
 * preserved from the current route. Facets/ranges are intentionally replaced,
 * not merged, so an accepted alias cannot remain beside its canonical value
 * and a rejected value cannot look selected.
 */
export const canonicalCatalogQueryFromApplied = (
  query: QueryObject,
  payload: Pick<CatalogSearchPayload, "facets" | "applied">
): QueryObject => {
  const facets = Array.isArray(payload.facets) ? payload.facets : [];
  const { selection, groupMap } = decodeCatalogSelection(query, facets);
  const groupByKey = new Map(groupMap.map((entry) => [entry.key, entry]));
  const canonical: Selection = {
    facets: {},
    ranges: {},
    price: {},
    search: selection.search,
    sort: selection.sort,
    order: selection.order,
    page: selection.page,
  };
  const finiteNumber = (value: unknown) => {
    if (value === null || value === undefined || value === "") return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  };

  for (const applied of payload.applied || []) {
    const key = String(applied?.key || "").trim();
    if (key === "price") {
      const min = finiteNumber(applied?.min);
      const max = finiteNumber(applied?.max);
      if (min !== undefined) canonical.price.min = min;
      if (max !== undefined) canonical.price.max = max;
      continue;
    }

    const group = groupByKey.get(key);
    if (!group) continue;
    if (group.kind === "range") {
      const min = finiteNumber(applied?.min);
      const max = finiteNumber(applied?.max);
      if (min !== undefined || max !== undefined) {
        canonical.ranges[key] = {
          ...(min !== undefined ? { min } : {}),
          ...(max !== undefined ? { max } : {}),
        };
      }
      continue;
    }

    const values = (applied?.values || [])
      .map((value) => String(value).trim())
      .filter((value) => /^[a-z0-9][a-z0-9_-]*$/.test(value));
    if (values.length) canonical.facets[key] = [...new Set(values)];
  }

  const encoded = encode(canonical, { groupMap });
  const passthrough = Object.fromEntries(
    Object.entries(query).filter(([rawKey, value]) => {
      const key = rawKey.toLowerCase();
      return (
        value !== undefined &&
        (key === "size" ||
          key === "category" ||
          NON_FILTER_PREFIXES.some((prefix) => key.startsWith(prefix)))
      );
    })
  );

  return { ...passthrough, ...encoded };
};

export const buildCatalogSearchInput = (
  query: QueryObject,
  options: {
    categoryId: string;
    page: number;
    pageSize: number;
    facets?: CatalogFacetDescriptor[];
    includeSubcategories?: boolean;
  }
) => {
  const { selection } = decodeCatalogSelection(query, options.facets || []);
  const filters: Array<{
    key: string;
    values?: string[];
    min?: string;
    max?: string;
  }> = [];

  for (const key of Object.keys(selection.facets).sort()) {
    const values = selection.facets[key];
    if (values?.length) {
      filters.push({ key, values: values.map(String) });
    }
  }
  for (const key of Object.keys(selection.ranges).sort()) {
    const range = selection.ranges[key];
    const filter: { key: string; min?: string; max?: string } = { key };
    if (range.min !== undefined) filter.min = String(range.min);
    if (range.max !== undefined) filter.max = String(range.max);
    if (filter.min !== undefined || filter.max !== undefined)
      filters.push(filter);
  }
  if (selection.price.min !== undefined || selection.price.max !== undefined) {
    const price: { key: string; min?: string; max?: string } = { key: "price" };
    if (selection.price.min !== undefined)
      price.min = String(selection.price.min);
    if (selection.price.max !== undefined)
      price.max = String(selection.price.max);
    filters.push(price);
  }

  const sort = selection.sort
    ? `${selection.sort}|${String(selection.order || "asc").toUpperCase()}`
    : undefined;

  return {
    categoryId: String(options.categoryId || ""),
    // OpenCart's existing category pages are exact-category listings. A caller
    // must opt in when it deliberately wants descendants as well.
    includeSubcategories: options.includeSubcategories === true,
    filters,
    page: Math.max(1, Number(options.page) || 1),
    pageSize: Math.max(1, Number(options.pageSize) || 1),
    ...(selection.search ? { search: selection.search } : {}),
    ...(sort ? { sort } : {}),
  };
};

export class UnmappedLegacyFilterError extends Error {
  keys: string[];

  constructor(keys: string[]) {
    super(`Legacy filter API cannot safely apply: ${keys.join(", ")}`);
    this.name = "UnmappedLegacyFilterError";
    this.keys = keys;
  }
}

/** Converts stable URL state to the old productsList variables, fail-closed. */
export const buildLegacyCatalogVariables = (
  query: QueryObject,
  base: Record<string, unknown>
) => {
  const { selection, groupMap } = decodeCatalogSelection(query);
  const knownByKey = new Map(FACET_GROUPS.map((entry) => [entry.key, entry]));
  const unsupported = groupMap
    .filter((entry) => !knownByKey.has(entry.key))
    .filter((entry) => {
      const facetValues = selection.facets[entry.key];
      const range = selection.ranges[entry.key];
      return (
        (facetValues && facetValues.length > 0) ||
        range?.min !== undefined ||
        range?.max !== undefined
      );
    })
    .map((entry) => entry.key);

  if (unsupported.length) throw new UnmappedLegacyFilterError(unsupported);

  const rawLegacyFilterIds = [query.filters, query.filter]
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .flatMap((value) => String(value || "").split(","))
    .map((value) => value.trim())
    .filter(Boolean);
  if (rawLegacyFilterIds.some((value) => !/^\d+$/.test(value))) {
    throw new UnmappedLegacyFilterError(["filters"]);
  }

  // The old productsList contract accepts flat OpenCart option ids directly.
  // Preserve them for a genuine missing-contract fallback; decodeBestEffort
  // cannot group them without descriptors and must never turn that ambiguity
  // into an unfiltered request.
  const filterIds: number[] = rawLegacyFilterIds.map(Number);
  const minFilters: Array<{ id: string; value: string }> = [];
  const maxFilters: Array<{ id: string; value: string }> = [];

  for (const [key, values] of Object.entries(selection.facets)) {
    if (!knownByKey.has(key)) continue;
    const numericValues = values.filter(
      (value): value is number =>
        typeof value === "number" && Number.isInteger(value)
    );
    if (numericValues.length !== values.length) {
      throw new UnmappedLegacyFilterError([key]);
    }
    filterIds.push(...numericValues);
  }
  for (const [key, range] of Object.entries(selection.ranges)) {
    const entry = knownByKey.get(key);
    if (!entry) continue;
    if (range.min !== undefined) {
      minFilters.push({ id: String(entry.groupId), value: String(range.min) });
    }
    if (range.max !== undefined) {
      maxFilters.push({ id: String(entry.groupId), value: String(range.max) });
    }
  }

  return {
    ...base,
    ...(filterIds.length ? { filter: [...new Set(filterIds)].join(",") } : {}),
    ...(minFilters.length ? { min_filters: minFilters } : {}),
    ...(maxFilters.length ? { max_filters: maxFilters } : {}),
    ...(selection.price.min !== undefined
      ? { min_price: selection.price.min }
      : {}),
    ...(selection.price.max !== undefined
      ? { max_price: selection.price.max }
      : {}),
    ...(selection.search ? { search: selection.search } : {}),
  };
};

const errorText = (error: unknown) => {
  const candidate = error as any;
  const graphQLErrors = Array.isArray(candidate?.graphQLErrors)
    ? candidate.graphQLErrors.map((item: any) => item?.message || item)
    : [];
  return [candidate?.message, ...graphQLErrors].filter(Boolean).join(" ");
};

export const isCatalogSearchContractUnavailable = (error: unknown) =>
  /cannot query field ["']?catalogsearchv1|unknown field ["']?catalogsearchv1|unknown type ["']?catalogsearchinput|catalogsearchv1.*(?:undefined|not found)/i.test(
    errorText(error)
  );

export class CatalogSearchUnavailableError extends Error {
  payload: CatalogSearchPayload | null;

  constructor(message: string, payload: CatalogSearchPayload | null = null) {
    super(message);
    this.name = "CatalogSearchUnavailableError";
    this.payload = payload;
  }
}

/** A request the Filter Orchestrator rejected as an invalid filter selection. */
export const isInvalidCatalogSelectionError = (error: unknown) =>
  error instanceof CatalogSearchUnavailableError &&
  (error.payload?.errors || []).some(
    (issue) => issue?.code === "INVALID_SELECTION"
  );

/**
 * True when the validator rejected a page, sort or search that the URL itself
 * sends: the display page (category.vue's SSR page arithmetic), the sort or
 * the search (buildCatalogSearchInput). The patterns are the starts of
 * selection_validator.php's own messages for those three checks. Any other
 * rejection (a pageSize over the configured limit, a non-numeric category id)
 * comes from the storefront or the module, not from the URL, and a message
 * this does not know stays a 503 as before.
 */
const rejectsListingUrlParameter = (
  error: CatalogSearchUnavailableError,
  query: QueryObject,
  facets: CatalogFacetDescriptor[]
) => {
  const displayPage = Math.max(1, Number(query?.page || 1) || 1);
  const { selection } = decodeCatalogSelection(query, facets);
  return (error.payload?.errors || []).some((issue) => {
    if (issue?.code !== "INVALID_SELECTION") return false;
    const message = String(issue?.message || "");
    return (
      (displayPage > 1 && /^page must be between /.test(message)) ||
      (!!selection.sort && /^Unsupported sort "/.test(message)) ||
      (!!selection.search && /^search must not exceed /.test(message))
    );
  });
};

/**
 * What a listing server render answers after its catalog request failed:
 * - "keep": the 200 page with the message. Only for a filter selection the
 *   Filter Orchestrator rejected on a URL that asks for a filter (such facet
 *   URLs are noindex anyway).
 * - "not-found": 404 + noindex. The validator rejected the page, sort or
 *   search of a URL that asks for no filter (a page past its bound, a legacy
 *   OpenCart sort such as sort=p.price, an over-long search). Asking again
 *   can never succeed, and a 503 would tell crawlers to keep retrying.
 * - "fail": 503 + noindex + Retry-After for everything else, including a
 *   rejection that no parameter of the URL explains: the fault is systemic.
 */
export type ListingRenderAfterError = "keep" | "not-found" | "fail";

export const listingRenderAfterError = (
  error: unknown,
  query: QueryObject,
  facets: CatalogFacetDescriptor[] = []
): ListingRenderAfterError => {
  if (!isInvalidCatalogSelectionError(error)) return "fail";
  if (hasRequestedCatalogFilters(query, facets)) return "keep";
  return rejectsListingUrlParameter(
    error as CatalogSearchUnavailableError,
    query,
    facets
  )
    ? "not-found"
    : "fail";
};

/** The "keep" case of listingRenderAfterError. */
export const keepsListingRenderAfterError = (
  error: unknown,
  query: QueryObject,
  facets: CatalogFacetDescriptor[] = []
) => listingRenderAfterError(error, query, facets) === "keep";

/**
 * The error a listing server render throws for listingRenderAfterError's
 * "not-found": server.js getSsrStatusCode answers a message that starts
 * with "404:" with 404 + noindex. The path only: the query may carry a
 * shopper's search text, which does not belong in the server log.
 */
export const listingNotFoundRenderError = (path: string) =>
  new Error(
    `404: ${
      String(path || "/").split(/[?#]/, 1)[0]
    } (catalog request parameters rejected)`
  );

export const requireOkCatalogSearch = (
  payload: CatalogSearchPayload | null
) => {
  if (!payload || payload.status !== "OK") {
    const status = payload?.status || "UNAVAILABLE";
    const reason = payload?.statusReason || "catalog_search_unavailable";
    throw new CatalogSearchUnavailableError(
      `Filter Orchestrator returned ${status}: ${reason}`,
      payload
    );
  }
  return payload;
};

const safeDiagnosticText = (value: unknown) =>
  String(value || "")
    .replace(/[\u0000-\u001f\u007f<>]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 180);

/** Shopper-safe error copy with a support reference, never raw stack output. */
export const catalogSearchUserMessage = (
  payload: CatalogSearchPayload | null,
  fallback = "Filters are temporarily unavailable. Please retry in a moment."
) => {
  const invalidSelection = (payload?.errors || []).find(
    (issue) => issue?.code === "INVALID_SELECTION"
  );
  const detail = safeDiagnosticText(invalidSelection?.message);
  const requestId = String(payload?.requestId || "").replace(
    /[^a-zA-Z0-9_-]/g,
    ""
  );
  const message = detail
    ? `We couldn't apply this filter selection: ${detail}`
    : fallback;
  return requestId ? `${message} Reference: ${requestId}.` : message;
};

/** Converts old OpenCart image values to safe browser URLs. */
export const normalizeCatalogFacetImage = (value: unknown) => {
  const image = String(value || "").trim();
  if (!image || /[\u0000-\u001f\u007f\\]/.test(image)) return "";
  if (/^https:\/\/[^\s]+$/i.test(image)) return image;
  if (
    /^data:image\/(?:png|gif|jpe?g|webp|avif|svg\+xml);base64,[a-z0-9+/=\s]+$/i.test(
      image
    )
  ) {
    return image;
  }
  if (image.startsWith("/image/")) return image;
  if (image.startsWith("image/")) return `/${image}`;
  if (image.startsWith("catalog/")) return `/image/${image}`;
  return "";
};

export const catalogPayloadToProducts = (payload: CatalogSearchPayload) => {
  const content = Array.isArray(payload.items) ? payload.items : [];
  const pageSize = Math.max(1, Number(payload.pageSize) || content.length || 1);
  const total = Math.max(0, Number(payload.total) || 0);
  return {
    content,
    size: pageSize,
    number: Math.max(1, Number(payload.page) || 1),
    totalPages: Math.ceil(total / pageSize),
    totalElements: total,
  };
};

export const normalizeCatalogFacets = (payload: CatalogSearchPayload) =>
  (Array.isArray(payload.facets) ? payload.facets : []).filter((facet) =>
    STABLE_KEY.test(String(facet?.key || ""))
  );

export const normalizeCatalogRange = (input: {
  min?: number;
  max?: number;
  availableMin?: number;
  availableMax?: number;
  changed: "min" | "max";
}) => {
  const finite = (value: number | undefined) =>
    value !== undefined && Number.isFinite(value) ? value : undefined;
  const availableMin = finite(input.availableMin);
  const availableMax = finite(input.availableMax);
  let min = finite(input.min);
  let max = finite(input.max);

  if (min !== undefined && availableMin !== undefined)
    min = Math.max(min, availableMin);
  if (min !== undefined && availableMax !== undefined)
    min = Math.min(min, availableMax);
  if (max !== undefined && availableMin !== undefined)
    max = Math.max(max, availableMin);
  if (max !== undefined && availableMax !== undefined)
    max = Math.min(max, availableMax);

  if (min !== undefined && max !== undefined && min > max) {
    if (input.changed === "min") min = max;
    else max = min;
  }

  return { min, max };
};

export const createLatestRequestGate = () => {
  let sequence = 0;
  return {
    begin: () => {
      sequence += 1;
      return sequence;
    },
    isLatest: (request: number) => request === sequence,
  };
};
